/**
 * `/parent/results` — the Parent Portal results view (P-SCH-V1, ADR-0103).
 *
 * The selected child's recent quiz results in a flat, read-only table (quiz ·
 * course · score · status · submitted), from `learning.learner.guardian.
 * childResults`. Pending (ungraded) attempts show a "Pending" pill instead of a
 * score. Read-only — a guardian never opens or re-takes a child's quiz.
 */
import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import type { inferRouterOutputs } from '@trpc/server';
import { DataTable, StatusPill, type DataTableColumn } from '@mangrove/ui';
import { trpc, type AppRouter } from '../lib/trpc.js';
import { ParentRoute } from '../components/parent-portal/parent-shell.js';
import { useSelectedChild, type ParentChild } from '../components/parent-portal/child-selector.js';
import {
  ChildScaffold,
  InlineError,
  formatDate,
  formatScorePercent,
  scoreTone,
} from '../components/parent-portal/parent-shared.js';

export const Route = createFileRoute('/parent/results')({
  component: ParentResultsPage,
});

type ResultRow =
  inferRouterOutputs<AppRouter>['learning']['learner']['guardian']['childResults']['rows'][number];

function ParentResultsPage(): React.JSX.Element {
  return (
    <ParentRoute title="Results">
      <ParentResultsContent />
    </ParentRoute>
  );
}

function ParentResultsContent(): React.JSX.Element {
  const { query, children, selected, setSelected } = useSelectedChild();
  return (
    <ChildScaffold
      heroTitle="Results"
      heroSubtitle="Your student’s recent quiz results across their courses."
      query={query}
      children={children}
      selected={selected}
      setSelected={setSelected}
      renderBody={(child) => <ResultsTable child={child} />}
    />
  );
}

function ResultsTable({ child }: { child: ParentChild }): React.JSX.Element {
  const resultsQ = trpc.learning.learner.guardian.childResults.useQuery({
    studentUserId: child.studentUserId,
  });
  const rows = resultsQ.data?.rows ?? [];

  const columns: DataTableColumn<ResultRow & { _i: number }>[] = [
    {
      key: 'quiz',
      header: 'Quiz',
      cell: (r) => <span className="font-medium text-foreground">{r.quizTitle}</span>,
    },
    {
      key: 'course',
      header: 'Course',
      cell: (r) => <span className="text-muted-foreground">{r.courseTitle}</span>,
    },
    {
      key: 'score',
      header: 'Score',
      cell: (r) => {
        const graded = r.status === 'graded' && r.scorePercent != null;
        if (!graded) return <StatusPill label="Pending" tone="warning" size="sm" />;
        return (
          <span className="tabular-nums font-semibold text-foreground">
            {formatScorePercent(r.scorePercent)}
          </span>
        );
      },
    },
    {
      key: 'status',
      header: 'Status',
      cell: (r) => {
        const graded = r.status === 'graded' && r.scorePercent != null;
        return graded ? (
          <StatusPill label="Graded" tone={scoreTone(r.scorePercent ?? 0)} size="sm" />
        ) : r.status === 'submitted' ? (
          <StatusPill label="Submitted" tone="neutral" size="sm" />
        ) : (
          <StatusPill label="In progress" tone="neutral" size="sm" />
        );
      },
    },
    {
      key: 'submitted',
      header: 'Submitted',
      cell: (r) => (
        <span className="text-muted-foreground">
          {r.submittedAt ? formatDate(r.submittedAt) : '—'}
        </span>
      ),
    },
  ];

  if (resultsQ.isError) return <InlineError onRetry={() => void resultsQ.refetch()} />;

  return (
    <DataTable
      columns={columns}
      rows={rows.map((r, i) => ({ ...r, _i: i }))}
      rowKey={(r) => `${r.quizTitle}-${r._i}`}
      isLoading={resultsQ.isPending}
      emptyTitle="No results yet"
      emptyDescription="Quiz results will appear here once your student completes a quiz."
      showIndex
    />
  );
}
